import { Reply } from "lucide-react";
import {
    animate,
    type MotionValue,
    motion,
    type PanInfo,
    useMotionValue,
    useTransform,
} from "motion/react";
import { type ReactNode, useRef } from "react";
import { cn } from "../../../../lib/cn";
import { useChat } from "../../ChatContext";
import { getReplyPreviewText } from "../Content";
import { useMessage } from "../Context";
import { useIsRtl } from "../useIsRtl";
import { useIsTouchDevice } from "../useIsTouchDevice";
import { useMessageSelection } from "./Selection";

const INDICATOR_SIZE_PX = 40;
const INDICATOR_PADDING_PX = 12;
const INDICATOR_SNAP_DISTANCE_PX =
    INDICATOR_PADDING_PX + INDICATOR_SIZE_PX + INDICATOR_PADDING_PX;
const INDICATOR_APPEAR_DELAY_PX = 24;
const INDICATOR_ENTER_DISTANCE_PX = 20;
const INDICATOR_INITIAL_OPACITY = 0.55;
const INDICATOR_INITIAL_SCALE = 0.85;
const INDICATOR_FOLLOW_FACTOR = 0.12;
const INDICATOR_FOLLOW_FACTOR_ARMED = 0.04;
const REPLY_THRESHOLD_PX = INDICATOR_SNAP_DISTANCE_PX + 24;
const MAX_DRAG_PX = 152;
const RUBBER_BAND_FACTOR = 0.35;
const THRESHOLD_BOUNCE_SCALE = 1.11;
const RELEASE_EASE = [0.32, 0.72, 0, 1] as const;
const RELEASE_DURATION_S = 0.28;

type SwipeToReplyProps = {
    children: ReactNode;
};

export function SwipeToReply({ children }: SwipeToReplyProps) {
    const { labels } = useChat();
    const {
        id,
        message,
        messageHtml,
        attachments,
        onReply,
        replySwipeFrom,
        deliveryStatus,
    } = useMessage();
    const selection = useMessageSelection();
    const isTouchDevice = useIsTouchDevice();
    const isRtl = useIsRtl();
    const isSelected = selection.state.selectedMessageId === id;
    const canSwipe =
        isTouchDevice &&
        Boolean(onReply) &&
        !isSelected &&
        deliveryStatus !== "sending" &&
        deliveryStatus !== "failed";
    const isFromRight = (replySwipeFrom ?? (isRtl ? "right" : "left")) === "right";

    const hasCrossedReplyThresholdRef = useRef(false);

    const x = useMotionValue(0);
    const bounceScale = useMotionValue(1);
    const indicatorOpacity = useTransform(x, (offset) =>
        getIndicatorOpacity(Math.abs(offset)),
    );
    const indicatorBaseScale = useTransform(x, (offset) =>
        getIndicatorBaseScale(Math.abs(offset)),
    );
    const indicatorScale = useTransform(
        [indicatorBaseScale, bounceScale],
        ([baseScale, bounce]: number[]) => baseScale * bounce,
    );
    const indicatorX = useTransform(x, (offset) => getIndicatorOffset(offset));

    function handleDrag(_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) {
        const pullDistance = dampenSwipeOffset(
            isFromRight ? -info.offset.x : info.offset.x,
        );
        x.set(isFromRight ? -pullDistance : pullDistance);

        if (pullDistance >= REPLY_THRESHOLD_PX) {
            if (!hasCrossedReplyThresholdRef.current) {
                hasCrossedReplyThresholdRef.current = true;
                playThresholdBounce(bounceScale);
            }
            return;
        }

        hasCrossedReplyThresholdRef.current = false;
    }

    function handleDragEnd(
        _event: MouseEvent | TouchEvent | PointerEvent,
        info: PanInfo,
    ) {
        const shouldReply = Math.abs(x.get()) >= REPLY_THRESHOLD_PX;
        hasCrossedReplyThresholdRef.current = false;

        void animate(x, 0, {
            type: "tween",
            duration: RELEASE_DURATION_S,
            ease: RELEASE_EASE,
            velocity: info.velocity.x,
        });
        bounceScale.set(1);

        if (!shouldReply || !onReply) {
            return;
        }

        onReply(
            getReplyPreviewText({
                message,
                messageHtml,
                attachments,
                imageLabel: labels.replyImage,
                audioLabel: labels.replyAudio,
                fileFallbackLabel: labels.fileAttachmentFallback,
            }),
        );
    }

    if (!canSwipe) {
        return children;
    }

    return (
        <div className="relative isolate w-full overflow-x-visible">
            <motion.div
                aria-hidden
                className={cn(
                    "pointer-events-none absolute inset-y-0 z-0 flex items-center",
                    isFromRight ? "right-12" : "left-12",
                )}
                style={{
                    x: indicatorX,
                    opacity: indicatorOpacity,
                    scale: indicatorScale,
                }}
            >
                <div className="flex size-40 min-h-40 min-w-40 items-center justify-center rounded-full bg-surface-default text-text-primary">
                    <Reply
                        aria-hidden="true"
                        className={cn("size-24", isFromRight && "-scale-x-100")}
                    />
                </div>
            </motion.div>

            <motion.div
                className="relative z-10 touch-pan-y"
                style={{ x }}
                drag="x"
                dragElastic={false}
                dragMomentum={false}
                dragDirectionLock
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
            >
                {children}
            </motion.div>
        </div>
    );
}

function getIndicatorOpacity(distance: number): number {
    if (distance <= INDICATOR_APPEAR_DELAY_PX) {
        return 0;
    }

    if (distance >= REPLY_THRESHOLD_PX) {
        return 1;
    }

    const enter = Math.min(
        (distance - INDICATOR_APPEAR_DELAY_PX) / INDICATOR_ENTER_DISTANCE_PX,
        1,
    );
    return enter * INDICATOR_INITIAL_OPACITY;
}

function getIndicatorBaseScale(distance: number): number {
    if (distance >= REPLY_THRESHOLD_PX) {
        return 1;
    }

    if (distance <= INDICATOR_APPEAR_DELAY_PX) {
        return INDICATOR_INITIAL_SCALE;
    }

    const enter = Math.min(
        (distance - INDICATOR_APPEAR_DELAY_PX) / INDICATOR_ENTER_DISTANCE_PX,
        1,
    );
    return 0.8 + enter * (INDICATOR_INITIAL_SCALE - 0.8);
}

function getIndicatorOffset(messageOffset: number): number {
    const distance = Math.abs(messageOffset);
    const direction = Math.sign(messageOffset) || 1;

    if (distance <= REPLY_THRESHOLD_PX) {
        return direction * distance * INDICATOR_FOLLOW_FACTOR;
    }

    const beforeThreshold = REPLY_THRESHOLD_PX * INDICATOR_FOLLOW_FACTOR;
    const afterThreshold =
        (distance - REPLY_THRESHOLD_PX) * INDICATOR_FOLLOW_FACTOR_ARMED;
    return direction * (beforeThreshold + afterThreshold);
}

function playThresholdBounce(bounceScale: MotionValue<number>) {
    void animate(bounceScale, [1, THRESHOLD_BOUNCE_SCALE, 1], {
        duration: 0.2,
        times: [0, 0.35, 1],
        ease: ["easeOut", RELEASE_EASE],
    });
}

function dampenSwipeOffset(rawOffset: number): number {
    if (rawOffset <= 0) {
        return 0;
    }

    if (rawOffset <= INDICATOR_SNAP_DISTANCE_PX) {
        return rawOffset;
    }

    const overshoot = rawOffset - INDICATOR_SNAP_DISTANCE_PX;
    const remaining = MAX_DRAG_PX - INDICATOR_SNAP_DISTANCE_PX;
    const dampedOvershoot =
        remaining * (1 - 1 / ((overshoot * RUBBER_BAND_FACTOR) / remaining + 1));

    return INDICATOR_SNAP_DISTANCE_PX + dampedOvershoot;
}
